import {IBookmark, IBookmarkTag} from '@/common/types/IBookmark.ts';
import {BookmarkService} from '@/common/services/BookmarkService.ts';
import {SyncBookmark, syncService} from '../sync/SyncService';
import {authService} from '../auth/AuthService';
import {APP_CONSTANTS} from '../constants/AppConstants';
import {folderService} from '../folder/FolderService';

// Server bookmark interface to match the API response
interface ServerBookmark {
  _id: string;
  userId: string;
  url: string;
  title: string;
  description?: string;
  image?: string;
  tags: string[];
  categories: string[];
  folderId?: string;
  isFavorite: boolean;
  isArchived: boolean;
  lastSyncedAt: string;
  lastModifiedTime: string;
  isDeleted: boolean;
  source: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface IWebBookmarkService {
  createBookmark(bookmark: Partial<IBookmark>): Promise<{ id?: string } | undefined>;

  updateBookmark(bookmark: IBookmark): Promise<void>;

  updateTags(bookmarkId: string, tags: IBookmarkTag[], categories?: string[]): Promise<void>;

  getAllBookmarks(): Promise<IBookmark[]>;

  deleteBookmark(id: string): Promise<void>;
}

export class WebBookmarkService implements IWebBookmarkService {
  private bookmarkService: BookmarkService;

  constructor() {
    this.bookmarkService = new BookmarkService();
  }

  // Fetch tags and categories from API for a bookmark
  private async fetchTagsAndCategories(bookmark: IBookmark): Promise<{
    tags: IBookmarkTag[];
    categories: string[],
    folder: string
  } | null> {
    try {
      if (!authService.isAuthenticated()) {
        return null;
      }

      const token = authService.getToken();
      if (!token) {
        return null;
      }

      const response = await fetch(`${APP_CONSTANTS.API_BASE_URL}/metadata/tags/authenticated`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          url: bookmark.url,
          bookmark: bookmark,
          userFolders: (await folderService.getAll()).map(f => f.name).join(',')
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.data) {
          return {
            tags: data.data.tags.map((tag: string) => ({name: tag})),
            categories: data.data.categories || [],
            folder: data.data.folder,
          };
        }
      }
    } catch (error) {
      console.error('Error fetching tags and categories from API:', error);
    }
    return null;
  }

  // Auto-tag bookmarks that don't have tags or categories
  private async autoTagBookmark(bookmark: IBookmark): Promise<IBookmark> {
    // Check if bookmark needs auto-tagging (no tags or no categories)
    const needsTagging = !bookmark.tags || bookmark.tags.length === 0 ||
      !bookmark.categories || bookmark.categories.length === 0 || !bookmark.folderId;

    if (needsTagging) {
      const tagsAndCategories = await this.fetchTagsAndCategories(bookmark);
      if (tagsAndCategories) {
        const updatedBookmark = {
          ...bookmark,
          tags: bookmark.tags && bookmark.tags.length > 0 ? bookmark.tags : tagsAndCategories.tags,
          categories: bookmark.categories && bookmark.categories.length > 0 ? bookmark.categories : tagsAndCategories.categories,
          folderId: bookmark.folderId || (await folderService.getFolderByName(tagsAndCategories.folder))?.id
        };

        // Update the bookmark in local storage
        // await this.bookmarkService.updateBookmark(updatedBookmark);

        // Add to sync queue
        syncService.updateBookmark(updatedBookmark as SyncBookmark);

        return updatedBookmark;
      }
    }
    return bookmark;
  }

  // Transform server bookmark to local bookmark format
  private transformServerBookmark(serverBookmark: ServerBookmark): IBookmark {
    return {
      id: serverBookmark._id,
      url: serverBookmark.url,
      title: serverBookmark.title,
      description: serverBookmark.description,
      image: serverBookmark.image,
      source: serverBookmark.source,
      addedOn: new Date(serverBookmark.createdAt).getTime(),
      tags: serverBookmark.tags.map(tag => ({name: tag})),
      categories: serverBookmark.categories,
      folderId: serverBookmark.folderId,
      viewCount: 0,
      clickCount: 0,
      is_starred: serverBookmark.isFavorite ? 'true' : 'false',
      lastViewedOn: new Date(serverBookmark.lastModifiedTime).getTime(),
      type: 'url'
    };
  }

  // Get all bookmarks (local + sync with server)
  async getAllBookmarks(): Promise<IBookmark[]> {
    try {
      // First get local bookmarks
      const localBookmarks = await this.bookmarkService.getAllBookmarks();

      // If user is authenticated, try to fetch from server
      if (authService.isAuthenticated()) {
        try {
          const serverResponse = await syncService.fetchFromBackend();
          if (serverResponse.success && serverResponse.bookmarks) {
            // Transform server bookmarks to local format
            const serverBookmarks = (serverResponse.bookmarks as unknown as ServerBookmark[])
              .filter(bookmark => !bookmark.isDeleted) // Filter out deleted bookmarks
              .map(bookmark => this.transformServerBookmark(bookmark));

            // Create a map of local bookmarks by ID for quick lookup
            const localBookmarksMap = new Map(localBookmarks.map(b => [b.id, b]));

            // Merge server bookmarks, keeping local changes
            const mergedBookmarks: IBookmark[] = [];

            for (const serverBookmark of serverBookmarks) {
              const localBookmark = localBookmarksMap.get(serverBookmark.id);
              if (localBookmark) {
                // Keep local bookmark if it exists (local changes take precedence)
                mergedBookmarks.push(localBookmark);
                localBookmarksMap.delete(serverBookmark.id);
              } else {
                // Add server bookmark if not in local storage
                mergedBookmarks.push(serverBookmark);
              }
            }

            // Add remaining local bookmarks that weren't on server
            mergedBookmarks.push(...localBookmarksMap.values());

            // Auto-tag bookmarks that need it
            const autoTaggedBookmarks: IBookmark[] = [];
            for (const bookmark of mergedBookmarks) {
              const autoTaggedBookmark = await this.autoTagBookmark(bookmark);
              autoTaggedBookmarks.push(autoTaggedBookmark);
            }

            // Save merged and auto-tagged bookmarks to local storage
            this.saveBookmarksToLocalStorage(autoTaggedBookmarks);
            return autoTaggedBookmarks;
          }
        } catch (error) {
          console.error('Failed to fetch from server:', error);
        }
      }

      // Return local bookmarks if server fetch failed or user not authenticated
      return localBookmarks;
    } catch (error) {
      console.error('Error getting bookmarks:', error);
      return [];
    }
  }

  // Create bookmark (local + sync)
  async createBookmark(bookmarkData: Partial<IBookmark>): Promise<{ id?: string } | undefined> {
    try {
      // Create bookmark locally first
      const result = await this.bookmarkService.createBookmark(bookmarkData);

      if (result?.id) {
        // Get the created bookmark
        const newBookmark = await this.bookmarkService.getBookmark(result.id);

        if (newBookmark) {
          // Auto-tag the new bookmark
          const autoTaggedBookmark = await this.autoTagBookmark(newBookmark);

          // Add to sync queue
          syncService.addBookmark(autoTaggedBookmark as SyncBookmark);

          // Try to sync immediately if authenticated
          if (authService.isAuthenticated()) {
            try {
              await syncService.syncToBackend([autoTaggedBookmark as SyncBookmark]);
            } catch (error) {
              console.error('Failed to sync new bookmark:', error);
            }
          }
        }

        return result;
      }

      throw new Error('Failed to create bookmark');
    } catch (error) {
      console.error('Error creating bookmark:', error);
      return undefined;
    }
  }

  // Update bookmark (local + sync)
  async updateBookmark(bookmark: IBookmark): Promise<void> {
    try {
      // Update locally first
      await this.bookmarkService.updateBookmark(bookmark);

      // Add to sync queue
      syncService.updateBookmark(bookmark as SyncBookmark);

      // Try to sync immediately if authenticated
      if (authService.isAuthenticated()) {
        try {
          syncService.syncToBackend([bookmark as SyncBookmark]);
        } catch (error) {
          console.error('Failed to sync updated bookmark:', error);
        }
      }
    } catch (error) {
      console.error('Error updating bookmark:', error);
      throw error;
    }
  }

  // Update tags (local + sync)
  async updateTags(bookmarkId: string, tags: IBookmarkTag[], categories?: string[]): Promise<void> {
    try {
      // Update locally first
      await this.bookmarkService.updateTags(bookmarkId, tags, categories);

      // Get the updated bookmark
      const updatedBookmark = await this.bookmarkService.getBookmark(bookmarkId);

      if (updatedBookmark) {
        // Add to sync queue
        syncService.updateBookmark(updatedBookmark as SyncBookmark);

        // Try to sync immediately if authenticated
        if (authService.isAuthenticated()) {
          try {
            await syncService.syncToBackend([updatedBookmark as SyncBookmark]);
          } catch (error) {
            console.error('Failed to sync updated bookmark tags:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error updating tags:', error);
      throw error;
    }
  }

  // Delete bookmark (local + sync)
  async deleteBookmark(id: string): Promise<void> {
    try {
      // Get bookmark before deleting for sync
      const bookmark = await this.bookmarkService.getBookmark(id);

      // Delete locally first
      await this.bookmarkService.deleteBookmark(id);

      if (bookmark) {
        // Add to sync queue
        syncService.deleteBookmark(bookmark as SyncBookmark);

        // Try to sync immediately if authenticated
        if (authService.isAuthenticated()) {
          try {
            syncService.syncToBackend([]);
          } catch (error) {
            console.error('Failed to sync deleted bookmark:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error deleting bookmark:', error);
      throw error;
    }
  }

  // Helper method to save bookmarks to local storage
  private saveBookmarksToLocalStorage(bookmarks: IBookmark[]): void {
    try {
      localStorage.setItem('bookmarks', JSON.stringify(bookmarks));
    } catch (error) {
      console.error('Error saving bookmarks to local storage:', error);
    }
  }
}

export const webBookmarkService = new WebBookmarkService();
